import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toAbsoluteUrl } from '../helpers/AssetHelpers'
import './terms.css'

const LAST_UPDATED = 'August 1, 2026'
const SUPPORT_EMAIL = 'support@eyeconx.net'

const SECTIONS = [
    {
        id: 'acceptance',
        title: 'Acceptance of Terms',
        body: [
            'By accessing or using the EyeconX Clinic Portal (“Service”), you agree to these Terms & Conditions on behalf of yourself and the clinic or organization you represent. If you do not agree, do not use the Service.',
            'These Terms apply to clinic staff, administrators, and other authorized users who log in to manage patient communications and related practice workflows.',
        ],
    },
    {
        id: 'service',
        title: 'Description of the Service',
        body: [
            'EyeconX is a patient communication and engagement platform for eye care and related practices. The Service may include tools to send appointment reminders, recalls, notifications, surveys, thank-you messages, newsletters, educational content, and other outreach via email, SMS, and telephone.',
            'Depending on your clinic configuration, the Service may also support practice management system (PMS) data sync, patient file management, web bookings, reputation tools, templates, activity logs, media libraries, and SMS gateway features.',
            'Features available to your clinic depend on your subscription, enabled modules, and integration setup. EyeconX may update, improve, or discontinue features with reasonable notice when practicable.',
        ],
    },
    {
        id: 'accounts',
        title: 'Accounts and Access',
        body: [
            'Access is limited to authorized clinic users. You must provide accurate account information and keep login credentials confidential. You are responsible for all activity under your account.',
            'Clinic administrators are responsible for managing user access, roles, and timely deactivation of accounts when staff leave or no longer require access.',
            'Notify EyeconX promptly at the support email below if you suspect unauthorized access or a security incident related to your account.',
        ],
    },
    {
        id: 'clinic-responsibilities',
        title: 'Clinic Responsibilities',
        body: [
            'You are solely responsible for the content of communications you send through the Service and for ensuring that outreach complies with applicable laws, including consent, privacy, telemarketing, and anti-spam rules (for example, TCPA, CAN-SPAM, and similar regulations that apply to your practice location).',
            'You must obtain and maintain any patient consents required before sending SMS, email, or phone communications, and you must honor opt-out and communication preference requests.',
            'You are responsible for the accuracy of patient, appointment, and contact data entered into EyeconX or synced from your PMS, and for reviewing delivery failures or sync issues shown in Activity and PMS Update History.',
        ],
    },
    {
        id: 'data-privacy',
        title: 'Patient Data and Privacy',
        body: [
            'The Service may process patient information and other practice data necessary to provide reminders, recalls, messaging, and related features. EyeconX treats this information as confidential and implements administrative, technical, and physical safeguards appropriate to the nature of the Service.',
            'Where EyeconX acts as a service provider or business associate for protected health information, processing is governed by applicable agreements between EyeconX and your organization, including any Business Associate Agreement (BAA) in place.',
            'You remain responsible for your clinic’s privacy notices, patient rights requests, and internal policies. Do not use the Service to store or transmit data beyond what is reasonably needed for intended clinical communication and practice workflows.',
        ],
    },
    {
        id: 'acceptable-use',
        title: 'Acceptable Use',
        body: [
            'You agree not to misuse the Service. Prohibited conduct includes attempting to gain unauthorized access; interfering with Service integrity or performance; reverse engineering except where permitted by law; uploading malware; or using the Service for unlawful, harassing, deceptive, or spam communications.',
            'You may not use EyeconX to send messages that violate carrier or email provider policies, infringe intellectual property, or disclose patient information to unauthorized recipients.',
            'EyeconX may suspend or restrict access if misuse, security risk, or legal risk is reasonably suspected.',
        ],
    },
    {
        id: 'communications',
        title: 'Messaging and Delivery',
        body: [
            'Delivery of email, SMS, and phone communications depends on third-party carriers, networks, and patient devices. EyeconX does not guarantee that every message will be delivered, opened, or acted upon.',
            'Message timing, templates, and channels are configured by your clinic. You are responsible for reviewing templates and automated workflows before enabling them for patients.',
            'Carrier fees, patient messaging rates, and telephony charges may apply outside EyeconX and are the responsibility of the applicable party under your service arrangements.',
        ],
    },
    {
        id: 'integrations',
        title: 'Integrations and Third-Party Services',
        body: [
            'The Service may integrate with practice management systems and other third-party tools. Those systems are governed by their own terms and privacy policies. EyeconX is not responsible for third-party outages, data errors originating outside EyeconX, or changes made by third-party vendors.',
            'You authorize EyeconX to exchange data with configured integrations as needed to provide the Service, subject to your agreements with EyeconX and those vendors.',
        ],
    },
    {
        id: 'ip',
        title: 'Intellectual Property',
        body: [
            'EyeconX and its licensors own the Service, software, branding, templates provided by EyeconX, and related intellectual property. These Terms do not transfer ownership to you.',
            'Content you create or upload (such as clinic media, custom templates, or articles) remains yours. You grant EyeconX a limited license to host, process, and display that content solely to operate the Service for your clinic.',
        ],
    },
    {
        id: 'availability',
        title: 'Service Availability and Support',
        body: [
            'EyeconX aims to keep the Clinic Portal available and reliable, but the Service may be interrupted for maintenance, updates, network issues, or events beyond our reasonable control.',
            'Support is available through the contact channels provided in the portal and at the support email listed below. Response times may vary based on severity and support plan.',
        ],
    },
    {
        id: 'disclaimers',
        title: 'Disclaimers',
        body: [
            'THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, EYECONX DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
            'EyeconX does not provide medical advice. Communications and educational content sent through the Service are tools for your practice and do not replace professional clinical judgment.',
        ],
    },
    {
        id: 'liability',
        title: 'Limitation of Liability',
        body: [
            'To the maximum extent permitted by law, EyeconX and its officers, employees, and affiliates will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, lost revenue, lost data, or business interruption, arising from or related to use of the Service.',
            'EyeconX’s aggregate liability for claims relating to the Service will not exceed the fees paid by your organization to EyeconX for the Service during the twelve (12) months preceding the claim, except where liability cannot be limited under applicable law.',
        ],
    },
    {
        id: 'indemnity',
        title: 'Indemnification',
        body: [
            'You agree to indemnify and hold harmless EyeconX from claims, damages, losses, and expenses (including reasonable attorneys’ fees) arising from your content, your communications to patients, your failure to obtain required consents, misuse of the Service, or violation of these Terms or applicable law.',
        ],
    },
    {
        id: 'termination',
        title: 'Suspension and Termination',
        body: [
            'You may stop using the Service at any time subject to any active subscription or contract with EyeconX. EyeconX may suspend or terminate access for material breach, non-payment, security risk, or as required by law.',
            'Upon termination, your right to access the Clinic Portal ends. Provisions that by their nature should survive (including intellectual property, disclaimers, limitations of liability, and indemnity) will survive termination.',
        ],
    },
    {
        id: 'changes',
        title: 'Changes to These Terms',
        body: [
            'EyeconX may update these Terms from time to time. The “Last updated” date at the top of this page will reflect the latest revision. Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.',
            'If a material change requires additional notice under your contract with EyeconX, we will provide that notice as required.',
        ],
    },
    {
        id: 'governing-law',
        title: 'Governing Law',
        body: [
            'These Terms are governed by the laws applicable under your master agreement with EyeconX. If no governing law is specified in that agreement, the laws of the jurisdiction where EyeconX principally operates will apply, without regard to conflict-of-law principles.',
            'If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect.',
        ],
    },
    {
        id: 'contact',
        title: 'Contact',
        body: [
            `Questions about these Terms or the EyeconX Clinic Portal can be sent to ${SUPPORT_EMAIL}. You may also use the Contact Us page linked from the login screen.`,
        ],
    },
]

function TermsConditions() {
    const [activeId, setActiveId] = useState(SECTIONS[0].id)

    useEffect(() => {
        const root = document.getElementById('root')
        const previousRootHeight = root?.style.height
        const previousBodyOverflow = document.body.style.overflow

        if (root) root.style.height = 'auto'
        document.body.style.overflow = ''

        return () => {
            if (root) root.style.height = previousRootHeight || ''
            document.body.style.overflow = previousBodyOverflow
        }
    }, [])

    useEffect(() => {
        const elements = SECTIONS
            .map((section) => document.getElementById(section.id))
            .filter(Boolean)

        if (!elements.length) return undefined

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)

                if (visible[0]?.target?.id) {
                    setActiveId(visible[0].target.id)
                }
            },
            {
                rootMargin: '-20% 0px -55% 0px',
                threshold: [0.1, 0.25, 0.5],
            }
        )

        elements.forEach((element) => observer.observe(element))
        return () => observer.disconnect()
    }, [])

    return (
        <div className="terms-page">
            <header className="terms-page__nav">
                <div className="terms-page__nav-inner">
                    <Link to="/login" className="terms-page__brand">
                        <img
                            src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                            alt="EyeconX"
                        />
                    </Link>
                    <div className="terms-page__nav-actions">
                        <Link to="/contact-us" className="terms-page__nav-link">
                            Contact
                        </Link>
                        <Link to="/login" className="terms-page__nav-btn">
                            <i className="bi bi-box-arrow-in-right" />
                            Sign in
                        </Link>
                    </div>
                </div>
            </header>

            <section className="terms-page__hero">
                <div className="terms-page__hero-bg" aria-hidden="true">
                    <span className="terms-page__orb terms-page__orb--1" />
                    <span className="terms-page__orb terms-page__orb--2" />
                    <span className="terms-page__grid" />
                </div>
                <div className="terms-page__hero-inner">
                    <span className="terms-page__eyebrow">
                        <i className="bi bi-shield-check" />
                        Legal
                    </span>
                    <h1>Terms &amp; Conditions</h1>
                    <p>
                        How clinics and authorized users may access and use the EyeconX
                        Clinic Portal for patient communications, recalls, and practice workflows.
                    </p>
                    <div className="terms-page__hero-meta">
                        <span>
                            <i className="bi bi-calendar3" />
                            Updated {LAST_UPDATED}
                        </span>
                        <span>
                            <i className="bi bi-building" />
                            Clinic Portal
                        </span>
                        <span>
                            <i className="bi bi-file-earmark-text" />
                            {SECTIONS.length} sections
                        </span>
                    </div>
                </div>
            </section>

            <main className="terms-page__main">
                <div className="terms-page__layout">
                    <aside className="terms-page__toc" aria-label="Table of contents">
                        <div className="terms-page__toc-card">
                            <h2>On this page</h2>
                            <nav>
                                {SECTIONS.map((section, index) => (
                                    <a
                                        key={section.id}
                                        href={`#${section.id}`}
                                        className={`terms-page__toc-link${activeId === section.id ? ' is-active' : ''}`}
                                    >
                                        <span>{String(index + 1).padStart(2, '0')}</span>
                                        {section.title}
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    <div className="terms-page__content">
                        <div className="terms-page__notice">
                            <i className="bi bi-info-circle-fill" aria-hidden="true" />
                            <div>
                                <strong>Agreement notice</strong>
                                <p>
                                    By signing in or continuing to use EyeconX, you agree to these
                                    Terms on behalf of your clinic. Please review them carefully.
                                </p>
                            </div>
                        </div>

                        {SECTIONS.map((section, index) => (
                            <section
                                key={section.id}
                                id={section.id}
                                className="terms-page__section"
                            >
                                <div className="terms-page__section-label">
                                    <span>Section {String(index + 1).padStart(2, '0')}</span>
                                </div>
                                <h2>{section.title}</h2>
                                {section.body.map((paragraph, paragraphIndex) => (
                                    <p key={`${section.id}-${paragraphIndex}`}>{paragraph}</p>
                                ))}
                            </section>
                        ))}

                        <div className="terms-page__cta">
                            <div>
                                <h3>Questions about these Terms?</h3>
                                <p>
                                    Our team can help with Clinic Portal access, agreements, and account support.
                                </p>
                            </div>
                            <div className="terms-page__cta-actions">
                                <a href={`mailto:${SUPPORT_EMAIL}`} className="terms-page__cta-secondary">
                                    <i className="bi bi-envelope" />
                                    {SUPPORT_EMAIL}
                                </a>
                                <Link to="/login" className="terms-page__cta-primary">
                                    Back to Sign in
                                    <i className="bi bi-arrow-right" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="terms-page__footer">
                <div className="terms-page__footer-inner">
                    <span>© {new Date().getFullYear()} EyeconX. All rights reserved.</span>
                    <div className="terms-page__footer-links">
                        <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
                        <Link to="/contact-us">Contact Us</Link>
                        <Link to="/login">Sign in</Link>
                    </div>
                </div>
            </footer>
        </div>
    )
}

export default TermsConditions
